<template>
  <ul class="slo-pagination">
    <li v-if="!maxVisiblePagesExceedsTotalPages && !isInFirstPage" class="slo-pagination--item first-page">
      <button type="button" @click="handleClickFirstPage">
        <span>{{ 'First' | t }}</span>
      </button>
    </li>
    <li v-if="!maxVisiblePagesExceedsTotalPages && !isInFirstPage" class="slo-pagination--item previous-page">
      <button type="button" @click="handleClickPreviousPage">
        <span>
          <span class="md:hide">{{ 'Previous' | t }}</span>
          <SloIcon name="ArrowLeft" class="hide md:inline-flex" />
        </span>
      </button>
    </li>
    <li
      v-for="page in pages"
      :key="page.name"
      class="slo-pagination--item"
    >
      <button
        type="button"
        @click="handleClickPage(page.name)"
        :disabled="page.isDisabled"
        :class="{'slo-pagination--item--active': isPageActive(page.name)}"
      >
        <span>{{ page.name }}</span>
      </button>
    </li>
    <li
      v-if="!maxVisiblePagesExceedsTotalPages && !isInLastPage"
      class="slo-pagination--item next-page"
    >
      <button type="button" @click="handleClickNextPage">
        <span>
          <span class="md:hide">{{ 'Next' | t }}</span>
          <SloIcon name="ArrowRight" class="hide md:inline-flex" />
        </span>
      </button>
    </li>
    <li
      v-if="!maxVisiblePagesExceedsTotalPages && !isInLastPage"
      class="slo-pagination--item last-page"
    >
      <button type="button" @click="handleClickLastPage">
       <span>{{ 'Last' | t }}</span>
      </button>
    </li>
  </ul>
</template>

<script>
import t from '../../js/main/i18n';
import SloIcon from './SloIcon.vue';

export default {
  name: 'SloPaginator',
  components: {
    SloIcon
  },
  filters: {
    t
  },
  props: {
    totalPages: {type: Number, required: true},
    currentPage: {type: Number, required: true},
    maxVisiblePages: {type: Number, required: false, default: 5}
  },
  computed: {
    maxVisiblePagesExceedsTotalPages() {
      return this.maxVisiblePages >= this.totalPages;
    },
    startPage() {
      if (this.maxVisiblePages >= this.totalPages) {
        return 1;
      }

      return this.currentPage;
    },
    pages() {
      const range = [];

      let startPage = this.startPage;
      if (this.totalPages < (this.currentPage + this.maxVisiblePages)) {
        startPage = this.totalPages - this.maxVisiblePages;
        startPage = startPage < 1 ? 1 : startPage;
      }

      let totalVisiblePages = startPage
        + (startPage === 1 ? this.maxVisiblePages - 1 : this.maxVisiblePages);
      if (totalVisiblePages > this.totalPages) {
        totalVisiblePages = this.totalPages;
      }

      for (let i = startPage; i <= totalVisiblePages; i++) {
        range.push({name: i, isDisabled: i === this.currentPage});
      }

      return range;
    },
    isInFirstPage() {
      return this.currentPage === 1;
    },
    isInLastPage() {
      return this.currentPage === this.totalPages;
    }
  },
  methods: {
    handleClickFirstPage() {
      this.$emit('pageChanged', 1);
    },
    handleClickPreviousPage() {
      this.$emit('pageChanged', this.currentPage - 1);
    },
    handleClickPage(page) {
      this.$emit('pageChanged', page);
    },
    handleClickNextPage() {
      this.$emit('pageChanged', this.currentPage + 1);
    },
    handleClickLastPage() {
      this.$emit('pageChanged', this.totalPages);
    },
    isPageActive(page) {
      return this.currentPage === page;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../scss/variables';
@import '../../scss/system';

.slo-pagination {
  list-style-type: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.6rem;
  margin: 0 auto;
  padding: 0;
  overflow: hidden;

  @media (min-width: $screen-md) {
    justify-content: center;
    gap: initial;
    max-width: max-content;
    border: 1px solid $color-gray-400;
    border-radius: 0.8rem;
  }

  &--item {
    display: none;

    @media (min-width: $screen-md) {
      display: block;
    }

    &.previous–page, &.next–page {
      display: flex;
      min-width: 88px;
      border: 1px solid $color-gray-400;
      border-radius: 0.8rem;
      overflow: hidden;

      button {
        width: 100%;
      }

      @media (min-width: $screen-md) {
        min-width: auto;
        border: none;
        border-radius: unset;
        overflow: auto;
      }
    }

    @media (min-width: $screen-md) {
      &:not(:last-of-type) {
        border-right: 1px solid $color-gray-400;
      }
    }

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 1.2rem;
      background-color: transparent;
      color: $color-gray-700;
      border-radius: initial;
      transition: background-color 0.2s ease-in, color 0.2s ease-in;

      &:hover, &:focus, &.slo-pagination--item--active {
        background-color: $color-blue-50;
        color: $color-blue-400;
      }

      &:disabled {
        pointer-events: none;
      }

      span {
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 1.6rem;
        min-height: 2.4rem;
      }
    }
  }
}
</style>
